import EditRubricForm from 'components/Rubrics/EditRubricForm';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { memo, useCallback, useEffect, useState } from 'react';
import rubricService from 'services/rubric';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ResponsiveDialog,
    Stack,
    Text,
    styled,
} from 'shell/components';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getWordCount, isNatural } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const defaultForm = {
    title: '',
    criteria: [
        {
            id: uuid(),
            title: 'Criterion Name',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
            ],
        },
    ],
    sort: 'desc',
};

const defaultErrors = {
    title: '',
    criteria: {},
};

const EditRubricDialog = memo(({ open, rubric, customDefaultForm, onClose, onSave }) => {
    const { setRubrics } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultErrors);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [hasClickedSaveAsTemplate, setHasClickedSaveAsTemplate] = useState(false);

    const handleClose = () => {
        setDialog({
            open: true,
            message: 'You Have Unsaved Changes',
            description: 'If you exit without saving, all unsaved changes will be lost.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Exit',
            cancelButtonText: 'Cancel',
            onAction: () => {
                onClose();
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateTitle = useCallback(() => {
        const error = form.title.trim() ? '' : 'Please enter a name';
        setErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [form.title]);

    const validateCriteria = useCallback(() => {
        let error = false;
        form.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isNatural(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? 'Please enter a number' : '',
                                description: isDescriptionInvalid ? 'Word count exceeded' : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [form.criteria]);

    const validateAll = () => {
        const isTitleValid = validateTitle();
        const areCriteriaValid = validateCriteria();
        if (isTitleValid && areCriteriaValid) return true;
        return false;
    };

    const handleClickSaveTemplate = () => {
        const isValid = validateAll();
        if (isValid) {
            setHasClickedSaveAsTemplate(true);
            const timestamp = new Date().getTime();
            const rubric = {
                id: uuid(),
                active: true,
                created_on: timestamp,
                modified_on: timestamp,
                rubric: form,
                user_id: auth.user_id,
            };
            setRubrics((current) => [...current, rubric]);
            rubricService.createRubric(rubric);
            message.success('Rubric was saved as a template.');
        } else {
            message.error('One or more fields are not valid.');
        }
    };

    const handleClickSave = () => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            onSave(form);
            onClose();
        } else {
            message.error('One or more fields are not valid.');
        }
    };

    useEffect(() => {
        if (open) {
            if (rubric) {
                setForm(rubric);
            } else if (customDefaultForm) {
                setForm(customDefaultForm);
            } else {
                setForm(defaultForm);
            }
        }
    }, [customDefaultForm, open, rubric]);

    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setErrors(defaultErrors);
                setHasClickedSubmit(false);
            }, 150);
        }
    }, [open]);

    useEffect(() => {
        setHasClickedSaveAsTemplate(false);
    }, [form]);

    return (
        <>
            <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
                <DialogTitle>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            {!rubric ? 'Create a' : 'Edit'} Rubric
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    </Stack>
                </DialogTitle>
                <Divider />
                <CustomDialogContent>
                    <EditRubricForm
                        form={form}
                        setForm={setForm}
                        errors={errors}
                        setErrors={setErrors}
                        hasClickedSubmit={hasClickedSubmit}
                        validateTitle={validateTitle}
                        validateCriteria={validateCriteria}
                    />
                </CustomDialogContent>
                <Divider />
                <DialogActions>
                    <Stack direction={'row-reverse'} spacing={4}>
                        <Button onClick={handleClickSave}>{!rubric ? 'Create' : 'Update'} Rubric</Button>
                        <Button
                            disabled={hasClickedSaveAsTemplate}
                            tooltip={{
                                component:
                                    'This will save the current version of this rubric to your rubric templates, allowing you to use it as a template for future rubrics.',
                                maxWidth: '200px',
                            }}
                            variant={'secondary'}
                            onClick={handleClickSaveTemplate}
                        >
                            Save as a Template
                        </Button>
                    </Stack>
                </DialogActions>
            </CustomResponsiveDialog>
        </>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

export default EditRubricDialog;
